import {
    BLOCK_CODE_LINE,
    BLOCK_DIVIDER,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HEADINGS,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    code,
    expression,
    list,
    MARK_BOLD,
    MARK_CODE,
    MARK_ITALIC,
    MARK_STRIKETHROUGH,
    math,
    paragraph,
} from '@gitbook/doc-core';
import { resolveCodeBlockSyntax } from '@gitbook/react-uikit';
import { Block, type Change, type Leaf, type Value } from '@gitbook/slate';
import AutoReplace from '@gitbook/slate-auto-replace';

/**
 * `**...**` toggles bold
 */
const bold = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH],
    onInput: '*', // trigger only on '*' for perf
    before: /\*\*[^*]+?\*$/, // non-greedy
    transform: (change, _e, matches) => {
        if (
            hasMark(change.value, [MARK_BOLD, MARK_CODE]) ||
            isPotentiallyTypingCode(change.value)
        ) {
            return change.insertText('*'); // act as a noop
        }

        const text = matches.before[0].slice(2).slice(0, -1); // inside the *..*

        if (hasMarkAtMatchStart(change.value, text, [MARK_CODE, MARK_ITALIC])) {
            return change.insertText('*');
        }

        return change
            .deleteBackward(text.length + 3) // remove the typed text
            .addMark(MARK_BOLD)
            .insertText(text)
            .removeMark(MARK_BOLD);
    },
});

/**
 * `_..._` toggles italic
 */
const italic = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH],
    onInput: '_', // trigger only on '_' for perf
    before: /\b_[^_]+?$/, // non-greedy
    transform: (change, _e, matches) => {
        if (
            hasMark(change.value, [MARK_ITALIC, MARK_CODE]) ||
            isPotentiallyTypingCode(change.value)
        ) {
            return change.insertText('_'); // act as a noop
        }

        const text = matches.before[0].slice(1); // inside the _..._

        if (hasMarkAtMatchStart(change.value, text, [MARK_BOLD, MARK_CODE])) {
            return change.insertText('_');
        }

        return change
            .deleteBackward(text.length + 1) // remove the typed text
            .addMark(MARK_ITALIC)
            .insertText(text)
            .removeMark(MARK_ITALIC);
    },
});

/**
 * `~~...~~` toggles strikethrough
 */
const strikethrough = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH],
    onInput: '~', // trigger only on '~' for perf
    before: /~~[^*]+?~$/, // non-greedy
    transform: (change, _e, matches) => {
        if (
            hasMark(change.value, [MARK_STRIKETHROUGH, MARK_CODE]) ||
            isPotentiallyTypingCode(change.value)
        ) {
            return change.insertText('~'); // act as a noop
        }

        const text = matches.before[0].slice(2).slice(0, -1); // inside the ~~...~~

        if (hasMarkAtMatchStart(change.value, text, [MARK_STRIKETHROUGH, MARK_CODE])) {
            return change.insertText('~');
        }

        return change
            .deleteBackward(text.length + 3) // remove the typed text
            .addMark(MARK_STRIKETHROUGH)
            .insertText(text)
            .removeMark(MARK_STRIKETHROUGH);
    },
});

/**
 * '`...`' toggles inline code
 */
const inlineCode = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH],
    onInput: '`',
    before: /`[^`]+$/, // non-greedy
    transform: (change, _e, matches) => {
        if (hasMark(change.value, MARK_CODE)) {
            return change.insertText('`'); // noop
        }

        const text = matches.before[0].slice(1); // inside the `...`

        // if there's a space at the start or end don't replace with inline code
        if (/^\s/.test(text) || /\s$/.test(text)) {
            return change.insertText('`');
        }

        return change
            .deleteBackward(text.length + 1) // remove the typed text
            .addMark(MARK_CODE)
            .insertText(text)
            .removeMark(MARK_CODE);
    },
});

/**
 * `$$...$$` inserts an inline math
 */
const inlineMath = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH],
    onInput: '$', // trigger only on '*' for perf
    before: /\$\$[^*]+?\$$/, // non-greedy
    transform: (change, _e, matches) => {
        if (hasMark(change.value, MARK_CODE) || isPotentiallyTypingCode(change.value)) {
            return change.insertText('$'); // act as a noop
        }

        const formula = matches.before[0].slice(2).slice(0, -1); // inside the $$..$$

        if (hasMarkAtMatchStart(change.value, formula, [MARK_BOLD, MARK_CODE, MARK_ITALIC])) {
            return change.insertText('$');
        }

        // remove the typed text
        change.deleteBackward(formula.length + 3);

        const inlineNode = math.createMathInline(formula);

        change.insertInline(inlineNode);

        return change;
    },
});

/**
 * `{{...}}` inserts an inline expression
 */
const inlineExpression = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH],
    onInput: '}',
    before: /\{\{[^{}]+?\}$/,
    transform: (change, _e, matches) => {
        if (hasMark(change.value, MARK_CODE) || isPotentiallyTypingCode(change.value)) {
            return change.insertText('}');
        }

        const expressionText = matches.before[0].slice(2).slice(0, -1);

        if (
            hasMarkAtMatchStart(change.value, expressionText, [
                MARK_BOLD,
                MARK_CODE,
                MARK_ITALIC,
                MARK_STRIKETHROUGH,
            ])
        ) {
            return change.insertText('}');
        }

        change.deleteBackward(expressionText.length + 3);

        const inlineNode = expression.createExpression(expressionText);

        change.insertInline(inlineNode);

        return change;
    },
});

/**
 * `$$` inserts a block math
 */
const blockMath = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH],
    onHotkey: 'enter',
    before: /^\$\$$/,
    transform: (change) => {
        const block = math.createMathBlock();
        return replaceBlockAndFocusIt(change, block);
    },
});

/**
 * `- ` or `* ` inserts a list
 */
const unorderedList = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH],
    onInput: ' ',
    before: /^(-|\*)$/,
    transform: (change) =>
        hasListItemParent(change.value) ? change : list.wrapInList(change, BLOCK_LIST_UL),
});

/**
 * `1. ` inserts an ordered list
 */
const orderedList = AutoReplace({
    onlyIn: BLOCK_PARAGRAPH,
    onInput: ' ',
    before: /^(([1-9]{1}\d*)\.)$/,
    transform: (change, _e, matches) => {
        if (hasListItemParent(change.value)) {
            return change;
        }

        const start = Number.parseInt(matches.before[1], 10);
        return list.wrapInList(change, BLOCK_LIST_OL, start && start !== 1 ? { start } : {});
    },
});

/**
 * Typing `[ ]` or `[x]`:
 *   - in a top-level empty paragraph create a task list
 *   - in an existing list item, converts the list to a task list
 */
const taskList = AutoReplace({
    onlyIn: BLOCK_PARAGRAPH,
    onInput: ']', // trigger only on ']' for perf
    before: /^(\[(?:[ xX])?)$/,
    transform: (change, _e, matches) => {
        const wrappingList = list.getCurrentList(change.value);
        if (wrappingList) {
            change.setNodeByKey(wrappingList.key, {
                type: BLOCK_LIST_TASKS,
            });
        } else {
            // Not in a list, create list when empty.
            if (!change.value.isEmpty) {
                return change.insertText(`${matches.before[0]}]`);
            }
            change = list.wrapInList(change, BLOCK_LIST_TASKS);
        }

        const item = list.getCurrentItem(change.value);
        const checked = matches.before[0][1]?.toLowerCase() === 'x';

        return item ? list.checkTaskItem(change, item.key, checked) : change;
    },
});

/**
 * '```' makes a code block
 */
const codeBlock = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH],
    onInput: '`',
    before: /``/,
    after: /(.*)?$/,
    transform: (change, _e, matches) => {
        const syntaxOrFilename = matches.after[0].trim();
        const syntax = resolveCodeBlockSyntax(syntaxOrFilename);
        const block = code.createCodeBlock(
            syntaxOrFilename !== syntax.key ? (matches.after[0] ?? '') : '',
            { syntax: syntax.key }
        );

        // remove the last two backticks
        change.deleteBackward(2);

        const { startBlock } = change.value;
        return startBlock?.isEmpty
            ? replaceBlockAndFocusIt(change, block) // replace the block
            : change.insertBlock(block).collapseToEndOf(block); // don't replace the block
    },
});

/**
 * When code block was triggered by `enter` key it could also include syntax.
 * We now trigger code block from input of backtick but that means user can't include syntax.
 *
 * Here we handle a single line code block with a kebab case match,
 * if it is a found syntax match that differs from the current syntax, we replace the code block's syntax.
 */
const codeBlockSyntax = AutoReplace({
    onlyIn: [BLOCK_CODE_LINE],
    onHotkey: 'enter',
    before: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    transform: (change, _e, matches) => {
        const block = code.getCurrentCode(change.value);
        if (!block) {
            return change;
        }

        const syntaxOrFilename = matches.before[0].trim();
        const currentSyntax = block.data.get('syntax');
        // if the match is the same syntax or not a code block with a single line
        // split the line as if an enter key is pressed
        if (currentSyntax === syntaxOrFilename || block.blockNodes.size > 1) {
            const result = code.splitCodeLine(change);
            return result ? result : change;
        }
        // if the match is not a valid syntax
        // split the line as if an enter key is pressed
        const syntax = resolveCodeBlockSyntax(syntaxOrFilename);
        if (syntax.key !== syntaxOrFilename) {
            const result = code.splitCodeLine(change);
            return result ? result : change;
        }

        return change
            .deleteBackward(syntaxOrFilename.length)
            .setDataByKey(block.key, ['syntax'], syntax.key)
            .collapseToEndOf(block);
    },
});

/**
 * '--- Enter' inserts a divider
 */
const divider = AutoReplace({
    onlyIn: BLOCK_PARAGRAPH,
    onHotkey: 'enter',
    before: /^(---+)$/,
    transform: (change) => {
        const { startBlock } = change.value;
        if (!startBlock) {
            return;
        }

        const block = Block.create({
            type: BLOCK_DIVIDER,
            isVoid: true,
        });

        const emptyParagraph = paragraph.createParagraph();

        return change
            .insertBlock(block)
            .insertBlock(emptyParagraph)
            .collapseToStartOf(emptyParagraph)
            .removeNodeByKey(startBlock.key);
    },
});

/**
 * Typing '>' inserts a new quote
 */
const quote = AutoReplace({
    onlyIn: BLOCK_PARAGRAPH,
    onInput: ' ',
    before: /^(>)$/,
    transform: (change) => {
        return change.wrapBlock(BLOCK_QUOTE);
    },
});

/**
 * True if current (paragraph expected in this context) block's parent is a list item.
 */
function hasListItemParent(value: Value): boolean {
    const { startBlock, document } = value;
    if (!startBlock) {
        return false;
    }

    const parent = document.getParent(startBlock.key);
    return parent?.type === BLOCK_LIST_ITEM;
}

/*
 * `# `   makes a heading 1
 * `## `  makes a heading 2
 * `### ` makes a heading 3
 */
const headingLevels = [BLOCK_HEADING_1, BLOCK_HEADING_2, BLOCK_HEADING_3];
const headings = AutoReplace({
    onlyIn: [BLOCK_PARAGRAPH, ...BLOCK_HEADINGS],
    onInput: ' ',
    before: /^(#{1,3})$/,
    transform: (change, _e, matches) => {
        const [hashes] = matches.before;
        const level = hashes.length;
        const type = headingLevels[level - 1];
        return change.setBlocks({ type });
    },
});

/**
 * True if selection contains one of the given mark
 */
function hasMark(value: Value | Leaf, type: string | string[]): boolean {
    const types = typeof type === 'string' ? [type] : type;
    return value.marks.some((mark) => types.includes(mark.type));
}

/**
 * True if the current selection contains one of the given mark at start of the match
 */
function hasMarkAtMatchStart(value: Value, text: string, type: string | string[]): boolean {
    const { startText, startOffset } = value;
    if (!startText) {
        return false;
    }

    const offset = startOffset - text.length;
    const { leaf } = startText.searchLeafAtOffset(offset);

    if (!leaf) {
        return false;
    }

    return hasMark(leaf, type);
}

/**
 * True if the user is potentially typing an inline code.
 */
function isPotentiallyTypingCode(value: Value): boolean {
    const { startText, startOffset } = value;

    if (!startText) {
        return false;
    }

    const textBeforeCursor = startText.text.slice(0, startOffset);
    if (!textBeforeCursor) {
        return false;
    }

    if (textBeforeCursor.match(/^[^`]+$/)) {
        return false;
    }

    return true;
}

/**
 * Replace the current block by a new one.
 * Preserving focus (in case it was the only block in the document) by inserting and moving selection first.
 */
function replaceBlockAndFocusIt(change: Change, block: Block): Change | undefined {
    const { startBlock } = change.value;

    if (!startBlock) {
        return;
    }

    return change.insertBlock(block).collapseToStartOf(block).removeNodeByKey(startBlock.key);
}

/**
 * Plugins that can be used in the BasicEditor.
 * Not all features are supported there.
 */
export const markdownBasicPlugins = [
    bold,
    italic,
    strikethrough,
    inlineCode,
    unorderedList,
    orderedList,
];

export const markdownPlugins = [
    ...markdownBasicPlugins,
    taskList,
    headings,
    inlineExpression,
    inlineMath,
    blockMath,
    codeBlock,
    quote,
    divider,
];

export { codeBlockSyntax };
